import { beautifyMessageIfError, emitToWebservice } from './utilFunctions';
import { Event } from 'fs-api-client';
import { IS_BLEEDING_EDGE } from './constants';
import LogTypeEnum = Event.LogTypeEnum;

const MAX_MESSAGE_LIST_SIZE = 100;
const MESSAGE_THROTTLE_INTERVAL_MS = 1000;

declare var FS: any;

class Message {
    static nextId: number = 0;

    public readonly id: number;

    constructor(
        readonly source: string,
        readonly text: any,
        readonly type: LogTypeEnum
    ) {
        this.id = Message.nextId++;

        if (!text) { text = '[empty]'; return; }
        this.text = `[${this.getFormattedTimestamp()}, v${process.env.REACT_APP_VERSION}] [${source}] ${this.ensureStringFormat(text)}`;
    }

    send(): boolean {
        if (!this.canSend()) { return false; }

        try {
            FS.log(this.type.toString(), this.text);
        } catch (e) {
            console.error(`FullStory log failed: ${JSON.stringify(e)}`);
            return false;
        }
        return true;
    }

    print(): void {
        if (!IS_BLEEDING_EDGE) { return; }

        if (this.type === LogTypeEnum.Error) {
            console.error(this.text);
        } else {
            console.log(this.text);
        }
    }

    private canSend(): boolean {
        const fsObjectPropertyName = 'FS';
        const localFS = window[fsObjectPropertyName];
        return localFS && localFS.log && typeof localFS.log === 'function';
    }

    private getFormattedTimestamp(): string {
        let currentDate = new Date();
        let formattedDate =
            currentDate.getFullYear() + '-' +
            (currentDate.getMonth() + 1) +
            '-' + currentDate.getDate() +
            ' ' + currentDate.getHours() +
            ':' + currentDate.getMinutes() +
            ':' + currentDate.getSeconds() +
            ':' + currentDate.getMilliseconds();
        return formattedDate;
    }

    private ensureStringFormat(text: any): string | null {
        if (!text) { return null; }

        text = beautifyMessageIfError(text);

        return text;
    }
}

class LogManager {
    private static messageBuffer: Array<Message> = [];
    private static messagesAreBeingBuffered: boolean = false;

    static logMessageBuffer(): boolean {
        if (!this.messagesAreBeingBuffered) { return false; }

        for (const message of LogManager.messageBuffer) {
            const success = message.send();
            if (!success) { return false; }
        }
        LogManager.messageBuffer = [];
        LogManager.messagesAreBeingBuffered = false;
        return true;
    }

    constructor(private moduleName: string) {}

    log(
        message: any,
        emit: boolean = false,
        eventType: Event.EventTypeEnum = Event.EventTypeEnum.Generic
    ): void {
        this.handleMessage(
            this.moduleName,
            message,
            LogTypeEnum.Log,
            emit,
            eventType
        );
    }

    error(message: any): void {
        this.handleMessage(
            this.moduleName,
            message,
            LogTypeEnum.Error,
            true,
            Event.EventTypeEnum.Generic
        );
    }

    private handleMessage(
        moduleName: string,
        text: any,
        type: LogTypeEnum,
        emit: boolean,
        eventType: Event.EventTypeEnum
    ): void {
        const message = new Message(
            moduleName,
            text,
            type
        );
        this.distributeMessage(message, type, emit, eventType);
    }

    private distributeMessage(
        message: Message,
        type: LogTypeEnum,
        emit: boolean,
        eventType: Event.EventTypeEnum
    ): void {
        LogManager.logMessageBuffer();

        message.print();

        if (emit) {
            emitToWebservice(message.text, type, eventType);
        }

        const messageSent = message.send();
        if (!messageSent) {
            this.bufferMessage(message);
        }
    }

    private bufferMessage(message: Message): void {
        if (LogManager.messageBuffer.length >= MAX_MESSAGE_LIST_SIZE) {
            LogManager.messageBuffer.shift();
        }
        LogManager.messageBuffer.push(message);
        LogManager.messagesAreBeingBuffered = true;
    }
}

const intervalId = setInterval(
    () => {
        const success = LogManager.logMessageBuffer();
        if (success) { clearInterval(intervalId); }
    },
    MESSAGE_THROTTLE_INTERVAL_MS
);

export { LogManager };



// WEBPACK FOOTER //
// ./src/webportal/utils/LogManager.ts